import { type DataSnapshot, onValue, ref } from "firebase/database";
import { useEffect, useRef, useState } from "react";
import { getFirebase } from "./init.js";

export interface RtdbValue<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

export function useRtdbValue<T = unknown>(path: string | null): RtdbValue<T> {
  const [state, setState] = useState<RtdbValue<T>>({ data: null, loading: true, error: null });
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    if (!path) {
      setState({ data: null, loading: false, error: null });
      lastPath.current = null;
      return undefined;
    }
    if (lastPath.current !== path) {
      lastPath.current = path;
      setState({ data: null, loading: true, error: null });
    }
    const { db } = getFirebase();
    const r = ref(db, path);
    const unsub = onValue(
      r,
      (snap: DataSnapshot) => {
        setState({ data: snap.val() as T | null, loading: false, error: null });
      },
      (err) => {
        setState({ data: null, loading: false, error: err as Error });
      },
    );
    return () => {
      unsub();
    };
  }, [path]);

  return state;
}
